import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/auth';
import { homePathOf } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { EyeIcon, EyeOffIcon } from '@/components/ui/icons';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTranslation } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { authenticate } from './api';
import styles from './LoginPage.module.css';

/**
 * The login screen.
 *
 * The one thing to get right here is that a failed login arrives as **200 OK** with
 * an `errorCode` in the body. `authenticate()` returns a discriminated union so
 * this component cannot forget to check, and the difference between "wrong
 * password" and "the server is unreachable" survives all the way to the message
 * the reader sees.
 */

interface LocationState {
  from?: { pathname?: string };
}

export const LoginPage = () => {
  const { isAuthenticated, signIn, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Where the guard sent them from, so signing in continues the journey instead of
  // dropping them at the landing screen. Without one — a direct visit to /login —
  // home: the board for people, the map for a wall screen.
  const requested = (location.state as LocationState | null)?.from?.pathname;

  if (isAuthenticated) {
    return <Navigate to={requested ?? homePathOf(user)} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await authenticate(username.trim(), password);

      if (result.ok) {
        signIn(result.session);
        navigate(requested ?? homePathOf(result.session.userInfo), { replace: true });
        return;
      }

      // Every rejection reason collapses into one server response — wrong
      // password, unknown user, blocked account — so the reader gets one honest
      // sentence rather than a guess about which it was. A malformed response is
      // NOT that, and says so.
      setError(
        result.errorCode === 'MALFORMED_RESPONSE'
          ? t('auth.unexpectedResponse')
          : t('auth.invalidCredentials'),
      );
    } catch (caught) {
      // Reaching here means the request itself failed: the network, a proxy, or a
      // 500. Telling the reader their password was wrong would send them looking
      // in the wrong place.
      const status = caught instanceof ApiError ? caught.status : 0;
      setError(status === 0 ? t('auth.networkError') : t('auth.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>{t('app.name')}</h1>
            <p className={styles.tagline}>{t('app.tagline')}</p>
          </div>
          <div className={styles.settings}>
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </header>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <Field
            label={t('auth.username')}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
            disabled={isSubmitting}
          />

          <Field
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            disabled={isSubmitting}
            trailing={
              <button
                type="button"
                className={styles.reveal}
                onClick={() => setShowPassword((shown) => !shown)}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                // Not a Tab stop: it reveals what the reader just typed, and it sits
                // between the password field and the submit button.
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOffIcon className={styles.revealIcon} />
                ) : (
                  <EyeIcon className={styles.revealIcon} />
                )}
              </button>
            }
          />

          {/* `role="alert"` so it is announced when it appears: a reader whose
              focus is still in the password field would otherwise never learn why
              nothing happened. */}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isSubmitting || username.trim() === '' || password === ''}
          >
            {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>
      </div>
    </div>
  );
};
